import { ajvResolver } from '@hookform/resolvers/ajv'
import ListIcon from '@mui/icons-material/List'
import { Box, Grid, InputLabel, MenuItem, Paper, TextField } from '@mui/material'
import { FormAlert, FormHeader } from 'components'
import { IInviteDTO } from 'data/dtos/operation/i-invite-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import InviteSchema, { IInviteFormData } from 'schema/operation/invite'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const InviteForm: React.FC = () => {
  const [mainError, setMainError] = useState('')
  const [customersB, setCustomersB] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)

  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  // const validationSchema = yup.object().shape({
  //   customerId: yup.string().required('Campo obrigatório'),
  //   invitedName: yup.string().required('Campo obrigatório'),
  //   invitedEmail: yup.string().email('E-Mail Inválido').required('Campo obrigatório'),
  //   token: yup.string(),
  // })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<IInviteFormData>({
    resolver: ajvResolver(InviteSchema),
    defaultValues: {
      customerId: '',
      invitedName: '',
      invitedEmail: '',
    },
  })

  // initial load

  useEffect(() => {
    async function loadData() {
      // select Cliente

      await api
        .post('/customers/select')
        .then((response) => {
          const { data } = response.data
          return data
        })
        .then((customersResult) => {
          setCustomersB(customersResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    loadData()
  }, [])

  // main data load

  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data

      await api
        .get(`/invites/${id}`)
        .then((response) => {
          const { data } = response.data

          if (data.accountCreationDate) {
            setReadonly(true)
          }

          const inviteResult = {
            id: data.id,
            customerId: data.customerId.id,
            invitedName: data.invitedName,
            invitedEmail: data.invitedEmail,
            token: data.token,
          }

          return inviteResult
        })
        .then((inviteResult: IInviteDTO) => {
          reset(inviteResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
    }
  }, [params, params.id])

  // data save

  const onSubmit = useCallback(async (data: IInviteDTO) => {
    setIsLoading(true)

    const payLoad: IInviteDTO = {
      id: '',
      customerId: data.customerId,
      invitedName: data.invitedName,
      invitedEmail: data.invitedEmail,
      token: data.token,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/invites`, payLoad)
        .then(() => history.push('/invites'))
        .catch((error) => {
          setMainError(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!')
          return error.response?.data.data
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/invites', payLoad)
        .then(() => history.push('/invites/new'))
        .then(() => reset())
        .catch((error) => {
          setMainError(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!')
          return error.response?.data.data
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  const handleChange = (formField: any) => {
    setMainError('')
  }

  return (
    <Paper
      elevation={0}
      className={classes.paper}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid='form'
      >
        <FormHeader
          title='Convites Enviados'
          icon={ListIcon}
          backRoute='/invites'
          saveButtonLabel='Enviar'
          showSaveButton={!readonly}
          isLoading={isLoading}
          helpText='Dados coletados sobre os convites de acesso á plataforma.'
        />

        <FormAlert
          setMainError={setMainError}
          mainError={mainError}
        />

        <Grid
          container
          spacing={1}
          className={mainError === '' ? classes.formContainer : classes.formContainerWithError}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <InputLabel htmlFor='customerId'>Cliente</InputLabel>
            <TextField
              id='customerId'
              error={!!errors.customerId}
              helperText={errors?.customerId?.message}
              variant='outlined'
              margin='dense'
              size='small'
              autoFocus
              fullWidth={true}
              value={`${watch().customerId}`}
              select
              disabled={isLoading || readonly}
              {...register('customerId', {
                onChange: (e) => {
                  setValue('customerId', e.target.value)
                  handleChange(e)
                },
              })}
            >
              {customersB.map((customer: any) => (
                <MenuItem
                  key={customer.id}
                  value={customer.id}
                >
                  {customer.tradingName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            xl={6}
          >
            <InputLabel htmlFor='invitedName'>Nome do Convidado</InputLabel>
            <TextField
              id='invitedName'
              error={!!errors.invitedName}
              helperText={errors?.invitedName?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              disabled={isLoading || readonly}
              {...register('invitedName', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            xl={6}
          >
            <InputLabel htmlFor='invitedEmail'>Email do Convidado</InputLabel>
            <TextField
              id='invitedEmail'
              error={!!errors.invitedEmail}
              helperText={errors?.invitedEmail?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              disabled={isLoading || readonly}
              {...register('invitedEmail', { onChange: (e) => handleChange(e) })}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default InviteForm
